import { type FC, useMemo } from 'react';
import {
    ClickableIcon,
    IconVerticalMenu,
    Menu,
    type MenuItem,
    Spinner,
    Stub,
    type TestProps,
} from '@direct-frontend/components';
import { isNotNullOrUndefined } from '#shared/lib/array';
import {
    isSupportedIntegrationType,
    type SupportedIntegrationSubTypes,
    type SupportedIntegrationTypes,
    useSourceBlockContext,
} from '#shared/lib/integrations';
import { settingsInventorySourcesTabUrl } from '#shared/lib/routes';
import { useInventoryChangeActions } from './hooks/useInventoryChangeActions';
import { useAvailableSourcesToChange } from '../../hooks/useAvailableSourcesToChange';
import { useGetIntegrationSettings } from '../../hooks/useGetIntegrationSettings';

type InventoryChangeIntegrationTypeMenuProps = {
    isOnlyDisabled?: boolean;
    additionalIntegrationType?: SupportedIntegrationSubTypes;
    onAfterDelete?: VoidFunction;
    onOpenCompareWarehousesModal?: VoidFunction;
    onAfterChangeSource?: (type: SupportedIntegrationTypes) => void;
} & TestProps;

const settingsSourceItem: MenuItem = {
    key: 'settings_step',
    content: 'Настроить источник данных',
    url: settingsInventorySourcesTabUrl,
};

export const InventoryChangeIntegrationTypeMenu: FC<InventoryChangeIntegrationTypeMenuProps> = ({
    isOnlyDisabled = false,
    additionalIntegrationType,
    onAfterDelete,
    onOpenCompareWarehousesModal,
    onAfterChangeSource,
    'data-testid': dataTestId = 'InventoryChangeIntegrationTypeMenu',
}) => {
    const { data, isError, isFetching, isSuccess } = useGetIntegrationSettings();
    const { syncType } = useSourceBlockContext();
    const { isSaving, handleDelete, handleChangeSource, renderConfirmModal } = useInventoryChangeActions({
        additionalIntegrationType,
        onAfterDelete,
        onOpenCompareWarehousesModal,
        onAfterChangeSource,
    });

    const availableSourceItems = useAvailableSourcesToChange({
        handleChangeSource,
        integrationType: isSupportedIntegrationType(data?.system_type) ? data?.system_type : undefined,
        syncType,
    });

    const deleteSourceItem: MenuItem = useMemo(() => {
        return {
            key: 'disable_source',
            value: 'disable_source',
            content: 'Отключить источник',
            onAction: handleDelete,
        };
    }, [handleDelete]);

    const menuItems: MenuItem[] = useMemo(() => {
        if (isOnlyDisabled) {
            return [deleteSourceItem];
        }

        return [availableSourceItems, deleteSourceItem, settingsSourceItem].filter(isNotNullOrUndefined);
    }, [availableSourceItems, deleteSourceItem, isOnlyDisabled]);

    if (isSaving) {
        return <Spinner color="gray-2" size="20" />;
    }

    if (isSupportedIntegrationType(data?.system_type) && isError) {
        return null;
    }

    if ((isError || !isSuccess) && !isFetching) {
        return <Stub width={20} height={20} />;
    }

    return (
        <>
            {renderConfirmModal()}
            <Menu
                size="m"
                aria-label="Контекстное меню"
                items={menuItems}
                placement="bottom-end"
                data-testid={dataTestId}
                trigger={
                    <ClickableIcon role="supplementary" data-testid={dataTestId}>
                        <IconVerticalMenu size="20" />
                    </ClickableIcon>
                }
            />
        </>
    );
};
